import React, { useState, useEffect } from 'react';
import data from './transformed_data.json'
import { Helmet } from 'react-helmet'; // Add this import
import seedrandom from 'seedrandom';
import './Game.css';  // Import the CSS file

function Game() {
  const [gameData, setGameData] = useState([]);
  const [difficulty, setDifficulty] = useState('Medium');
  const [unplayedGames, setUnplayedGames] = useState([]);
  const [currentGame, setCurrentGame] = useState(null);
  const [currentGuess, setCurrentGuess] = useState([]);
  const [foundGroups, setFoundGroups] = useState({});
  const [attempts, setAttempts] = useState(4);
  const [message, setMessage] = useState('');
  const [animation, setAnimation] = useState(null);
  const [animationWords, setAnimationWords] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameId, setGameId] = useState(null); // Add this line
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [selectionStreak, setSelectionStreak] = useState([]);

  const levelColors = ['#fbc02d', '#8bc34a', '#93c5fd', '#c084fc'];
  const levelEmojis = ["🔵", "🟢", "🟡", "🔴", "🟣"]; // Choose your own emojis
  const [shakeAnimation, setShakeAnimation] = useState(false);
  // Add these new state variables at the top with other useState declarations
  // Modify the state declarations
  const [highlightedWords, setHighlightedWords] = useState([]);
  const [hintsUsed, setHintsUsed] = useState(0);

  // Update the getHint function
  const getHint = () => {
    if (hintsUsed >= 2) {
      setMessage("No more hints available!");
      return;
    }

    // Get all unmatched words (words not in foundGroups)
    const foundWords = Object.values(foundGroups).flat();
    const unmatchedWords = currentGame.startingGroups.flat()
      .filter(word => !foundWords.includes(word));

    // Find two words that belong to the same group
    let hintWords = [];
    for (const [groupName, group] of Object.entries(currentGame.groups)) {
      if (foundGroups[groupName]) continue;

      const availableGroupWords = group.members
        .filter(word => unmatchedWords.includes(word));

      if (availableGroupWords.length >= 2) {
        hintWords = availableGroupWords.slice(0, 2);
        break;
      }
    }

    if (hintWords.length === 2) {
      setHighlightedWords(hintWords);
      setHintsUsed(hintsUsed + 1);
    }
  };

 
  
  useEffect(() => {
    setTimeElapsed(0);  // Reset the timer when starting a new game
  
    // Other codes
  
    const timer = setInterval(() => {
      setTimeElapsed(timeElapsed => timeElapsed + 1);
    }, 1000);
  
    return () => clearInterval(timer);
  }, [gameId]);  // Only depend on gameId

  function mixGroups(gameId, difficulty) {
    const rng = seedrandom(gameId); // Create seeded random number generator
    let newGame = { groups: {}, startingGroups: [[], [], [], []] };
    let usedWords = new Set();
  
    // Define difficulty levels
    let levels = [];
    switch(difficulty){
      case "Easy":
        levels = [0, 1];
        break;
      case "Hard":
        levels = [2, 3];
        break;
      default: // Medium
        levels = [0, 1, 2, 3];
        break;
    }
    
    for(let i=0; i<4; i++) {
      let randomId, randomGame, groupKey;
  
      // Keep trying until a suitable group is found
      do {
        do {
          randomId = Math.floor(rng() * data.length);
        } while(randomId === gameId); // Ensure that we don't select the same game
  
        randomGame = data[randomId];
  
        do {
          groupKey = Object.keys(randomGame.groups)[Math.floor(rng() * Object.keys(randomGame.groups).length)];
        } while(!levels.includes(randomGame.groups[groupKey].level)); // Check if the group's level is included in the selected levels
  
      } while(randomGame.groups[groupKey].members.some(member => usedWords.has(member))); // Check if the members are already used
  
      newGame.groups = {...newGame.groups, [groupKey]: randomGame.groups[groupKey]};
          
      // Shuffle members into startingGroups
      randomGame.groups[groupKey].members.forEach((member, index) => {
        usedWords.add(member); // Add the member to the usedWords set
  
        let insertionRow;
        do {
          insertionRow = Math.floor(rng() * 4);
        } while (newGame.startingGroups[insertionRow].length === 4);
        newGame.startingGroups[insertionRow].push(member);
      });
    }
    
    return newGame;
  }
  
  const shuffleArray = (array) => {
    let currentIndex = array.length, temporaryValue, randomIndex;
    while (0 !== currentIndex) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  };
  
  useEffect(() => {
    setGameData(data);
    setTimeElapsed(0);
    setUnplayedGames([...Array(data.length).keys()]);
  
    const urlParams = new URLSearchParams(window.location.search);
    const gameId = urlParams.get('gameId');
    setGameId(gameId);
  
    const difficulty = urlParams.get('difficulty') || 'Easy'; // default to Medium
    setDifficulty(difficulty);
  
    let gameIndex;
  
    if (gameId) {
      gameIndex = gameId;
    } else {
      gameIndex = generateIndexConnections() % data.length;
    }
  
    const mixedGame = mixGroups(gameIndex, difficulty);
    setCurrentGame(mixedGame);
    setUnplayedGames(prevUnplayedGames => prevUnplayedGames.filter(index => index !== gameIndex));
    // Reset the timer and selection streak when a new game is started
    setSelectionStreak([]);
  }, [gameId]);
  

  const changeDifficulty = (newDifficulty) => {
    // Generate new random index
    const randomIndex = Math.floor(Math.random() * unplayedGames.length);
    const nextGameIndex = unplayedGames[randomIndex];
  
    // Get the current path
    const currentPath = window.location.pathname;
  
    // Update the URL to include new gameId and difficulty
    window.location.href = window.location.origin + currentPath + '?gameId=' + nextGameIndex + '&difficulty=' + newDifficulty;
  };

  function generateIndexConnections() {
    const D = 864e5;
    const e = new Date("16/03/2024");
    const t = (new Date()).setHours(0, 0, 0, 0) - e.setHours(0, 0, 0, 0);
    let n = Math.round(t / D);
    return n < 0 ? Math.abs(n) : n;
  }

   // Update handleWordClick to remove highlighting when word is selected
   const handleWordClick = word => {
    // Remove word from highlighted words when selected
    if (highlightedWords.includes(word)) {
      setHighlightedWords(highlightedWords.filter(w => w !== word));
    }

    if (currentGuess.includes(word)) {
      setCurrentGuess(currentGuess.filter(guess => guess !== word));
    } else if (currentGuess.length < 4) {
      setCurrentGuess([...currentGuess, word]);
    } else {
      return;
    }
  };

  const submitGuess = () => {
    if (currentGuess.length !== 4) {
      setMessage('You need to select 4 items before submitting');
      return;
    }
  
    // Ensure correct guess regardless of order
    const guessedGroup = Object.entries(currentGame.groups).find(([groupName, group]) => {
      // Check if all items in currentGuess exist in group.members and vice versa
      const isMatch =
        group.members.length === currentGuess.length &&
        group.members.every(member => currentGuess.includes(member)) &&
        currentGuess.every(member => group.members.includes(member));
      return isMatch;
    });
  
    if (guessedGroup) {
      // Handle correct guess
      const [groupName, groupData] = guessedGroup;
  
      setAnimation('success');
      setAnimationWords([...currentGuess]); // Retain user's selection order
  
      setTimeout(() => {
        setFoundGroups({
          ...foundGroups,
          [groupName]: groupData.members,
        });
  
        const remainingWords = currentGame.startingGroups
          .flat()
          .filter(word => !currentGuess.includes(word));
  
        setCurrentGame({
          ...currentGame,
          startingGroups: chunkArray(remainingWords, 4),
        });
  
        setMessage('Congratulations! You have found a group!');
        setCurrentGuess([]);
        setAnimation(null);
        setAnimationWords([]);
      }, 2200);
    } else {
      // Handle incorrect guess
      setShakeAnimation(true);
      setMessage('Incorrect guess, please try again');
  
      setTimeout(() => {
        setMessage(null);
        setShakeAnimation(false);
        setCurrentGuess([]);
      }, 1500);
  
      setAttempts(prevAttempts => {
        if (prevAttempts - 1 === 0) {
          setGameOver(true);
          setMessage('Game Over! All attempts used up!');
        }
        return prevAttempts - 1;
      });
  
      setAnimation('error');
      setAnimationWords(currentGuess);
  
      setTimeout(() => {
        setAnimation(null);
        setAnimationWords([]);
      }, 1000);
    }
  
    // Track word levels for analytics or streak tracking
    const wordLevels = currentGuess.map(word => {
      const groupName = Object.keys(currentGame.groups).find(
        key => currentGame.groups[key].members.includes(word)
      );
      const level = currentGame.groups[groupName]?.level || null;
      return { word, level };
    });
  
    setSelectionStreak([...selectionStreak, wordLevels]);
  };
  

  const AttemptsDisplay = ({ attempts }) => {
    const dots = Array.from({ length: attempts }, (_, index) => <div className="dot" key={index}></div>);

    return (
      <div className="attempts-display">
        {dots}
      </div>
    );
  };
  
  const startNewGame = () => {
    if (unplayedGames.length === 0) {
      setMessage('No more games left!');
      return;
    }
  
    const randomIndex = Math.floor(Math.random() * unplayedGames.length);
    const nextGameIndex = unplayedGames[randomIndex];
    setUnplayedGames(prevUnplayedGames => prevUnplayedGames.filter(index => index !== nextGameIndex));
  
    // Get the current path
    const currentPath = window.location.pathname;
  
    // Redirect to new page with gameId and difficulty as URL parameters
    window.location.href = window.location.origin + currentPath + `?gameId=${nextGameIndex}&difficulty=${difficulty}`;
  };

  const restartGame = () => {
    window.location.reload();
  };

  const resetGuess = () => {
    setCurrentGuess([]);
  };

  const copyResultsToClipboard = () => {
    // Create a string from the selection streak
    const resultsString = selectionStreak.map((guess, index) => 
      guess.map((wordObj) => `${levelEmojis[wordObj.level]}`).join('')
    ).join('');
    window.focus();
    // Copy the string to the clipboard
    navigator.clipboard.writeText('unscrambleit.net/connections-nyt-sports-edition-unlimited/ - ' + resultsString).then(function(){
      alert('Copied to Clipboard, you can now share the result in social media.');
    });
    // Show an alert message
  };
  
  

  return (
    <>
    <div className="game-container">
      {gameId && (
        <Helmet>
        </Helmet>
      )}
      
      <div class="game-header">
        <h2 className='fs-3 fw-bold text-light'>Connections NYT Sports Edition Unlimited</h2>
      </div>
      <div>
        <div className="difficulty d-flex justify-content-center text-center fw-bold my-3 text-light">Select Game Mode:</div>
        <div className="d-flex flex-nowrap gap-3 mb-3 ">
          <button
            className={`btn btn-outline-dark rounded-pill difficulty-button ${difficulty === 'Easy' ? 'active' : ''}`}
            onClick={() => changeDifficulty('Easy')}
          >
            Easy
          </button>
          <button
            className={`btn btn-outline-dark rounded-pill difficulty-button ${difficulty === 'Medium' ? 'active' : ''}`}
            onClick={() => changeDifficulty('Medium')}
          >
            Medium
          </button>
          <button
            className={`btn btn-outline-dark rounded-pill difficulty-button ${difficulty === 'Hard' ? 'active' : ''}`}
            onClick={() => changeDifficulty('Hard')}
          >
            Hard
          </button>
        </div>
      </div>
       <div className="top-g d-flex flex-wrap align-items-center justify-content-between mb-3 text-light">
        {/* First Column */}
        <div className="col-lg-4 col-md-4 col-12 text-center text-md-start">
          <p className="p-text mb-0">Create four groups of four.</p>
        </div>

        {/* Second Column */}
        <div className="col-lg-4 col-md-4 col-12 text-center my-2">
        <div className="game-time mb-0">
            Time: {`${Math.floor(timeElapsed / 60).toString().padStart(2, '0')}:${(timeElapsed % 60).toString().padStart(2, '0')}`}
          </div>
        </div>

        {/* Third Column */}
        <div className="col-lg-4 col-md-4 col-12 text-center text-md-end">
        <button
            className="btn btn-sm btn-outline-dark rounded-pill d-inline-flex align-items-center px-3"
            onClick={getHint}
            disabled={hintsUsed >= 2 || gameOver}
          >
            Hint ({2 - hintsUsed})
          </button>
         
        </div>
      </div>
       
      {Object.entries(foundGroups).map(([groupName, words], groupIndex) => (
        <div
          className="game-group"
          key={groupName}
          style={{ backgroundColor: levelColors[groupIndex % levelColors.length] }}
        >
          <h3 className="group-name mb-3">{groupName}</h3>
          <div className="group-members">
            {words.map((word, wordIndex) => (
              <span
                key={word.content}
                className={`group-member fade-in fade-in-delay-${wordIndex}`}
              >
                {word.content}
              </span>
            ))}
          </div>
        </div>
      ))}

      {currentGame && currentGame.startingGroups.map((group, groupIndex) => (
        <div className="game-board" key={groupIndex}>
          {group.map((word, wordIndex) => {
            // Calculate the animation delay index for correct guesses
            const animationIndex = animationWords.indexOf(word);
            const bounceClass = animation === 'success' && animationIndex !== -1
              ? `bounce-delay-${animationIndex}`
              : '';

            return (
              <button
                className={`game-item 
                  ${currentGuess.includes(word) ? 'selected' : ''} 
                  ${currentGuess.includes(word) && shakeAnimation ? 'shake' : ''}
                  ${highlightedWords.includes(word) ? 'hint-highlight' : ''}
                  ${bounceClass}
                  ${word.length === 8 ? 'size-8' : ''} 
                  ${word.length > 8 ? 'size-more' : ''}`}
                key={wordIndex}
                onClick={() => !gameOver && handleWordClick(word)}
              >
                {word.img ? (
                  <img
                    src={word.img}
                    alt={word.content}
                    className="group-member-image"
                  />
                ) : (
                  word.content
                )}
              </button>
            );
          })}
        </div>
      ))}


      {message && <div className="message">{message}</div>}
      {gameOver ? (
        <>
        <div className='d-flex flex-nowrap gap-3 btn-wrapper'>
          <button className="game-btn btn btn-outline-dark rounded-pill px-md-4 px-2" onClick={restartGame}>Restart</button>
          <button className="game-btn btn btn-outline-dark rounded-pill" onClick={startNewGame}>Start New Game</button>
          </div>
        </>
      ) : (
        <>
          {currentGame && Object.keys(foundGroups).length === Object.keys(currentGame.groups).length ? (
            <>
                <div className="congratulations">
                    <h2 className='fs-4'>Congratulations!</h2>
                    <p>You have found all groups!</p>
                    <div className="share">
                      {selectionStreak.map((guess, index) => (
                        <div key={index} className="share-row">
                          {guess.map((wordObj, i) => (
                            <span key={i} style={{ backgroundColor: levelColors[wordObj.level] }}>
                              
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  <button className="game-btn" style={{ backgroundColor:'#000000', color:'#fff', marginTop:'10px' }} onClick={copyResultsToClipboard}>Share Results</button>
                </div>
                <button className="game-btn" onClick={startNewGame}>Start New Game</button>
            </>
        
          ) : (
            <div className="btn-wrapper">
              <button className="game-btn submit-btn btn btn-outline-dark rounded-pill" onClick={submitGuess}>Submit</button>
              <button className="game-btn btn btn-outline-dark rounded-pill" onClick={resetGuess}>Deselect</button>
              <button className="game-btn btn btn-outline-dark rounded-pill" onClick={startNewGame}>Start New Game</button>
            </div>
          )}
          <div className="game-attempts text-light">Mistakes remaining: <AttemptsDisplay attempts={attempts} /></div>
        </>
      )}
    </div>
    </>
  );
}

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

export default Game;